import React, {useLayoutEffect, useRef} from 'react';
import {observer} from 'mobx-react';
import s from '../s';
import ATCButton from '../components/atc/atc-button';
import {getPriceDisplay} from '../pricing-request/price-display';
import {productHeadingText} from '../components/product-heading';
import {buildProductSlides} from '../components/slider/product-slides/product-slides';
import {productSlidesInput} from '../components/slider/product-slides/product-slides-input';
import {redesign} from './redesign-store';
import {questionsLeft, shipShort} from './copy';
import {beforeRedesignAddToCart, currentShipState} from './atc-row';
import {TruckIcon} from './icons';

/**
 * Root classes for the pinned bar (rules in redesign.scss): the page scrolls clear of the fixed bottom bar and the chat square rides
 * above it. The bar is always shown, so it sets both. DF4 adds them before the first paint on B pages (product.cshtml), with the bar's
 * usual height as the default (redesign.scss), so nothing moves when the bar mounts; the bar keeps the height current.
 */
const ON_CLASS = 'sticky-atc-bar-on';
const SHOWN_CLASS = 'sticky-atc-bar-shown';
const HEIGHT_VAR = '--sticky-atc-bar-h';

/**
 * The pinned bar at the bottom of the viewport, on desktop and phone: the frame's thumbnail, name and picks (or the questions left),
 * the ship time, the total and the control's Add To Cart (ATCButton).
 */
const PinnedBar: React.FC<{summary: string}> = observer(({summary}) => {
    const bar = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const root = document.documentElement;
        const el = bar.current;
        const setHeight = () => root.style.setProperty(HEIGHT_VAR, `${el ? el.offsetHeight : 0}px`);
        setHeight();
        root.classList.add(ON_CLASS, SHOWN_CLASS);
        let observer: ResizeObserver | undefined;
        if (el && typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(setHeight);
            observer.observe(el);
        }
        return () => {
            observer?.disconnect();
            root.classList.remove(ON_CLASS, SHOWN_CLASS);
            root.style.removeProperty(HEIGHT_VAR);
        };
    }, []);

    const open = redesign.open;
    const price = getPriceDisplay(s.ui);
    const ship = currentShipState();
    const thumb = s.ready ? buildProductSlides(productSlidesInput()).find(slide => slide.id === 'slide-prod')?.thumb.url : undefined;
    const note = open.length > 0 ? questionsLeft(open.length) : summary;

    return (
        <div
            ref={bar}
            className="pp3-bar"
            role="region"
            aria-label="Your frame"
        >
            <div className="pp3-bar-inner">
                {thumb && (
                    <img
                        className="pp3-bar-thumb"
                        src={thumb}
                        alt=""
                    />
                )}
                <p className="pp3-bar-name">
                    <span className="pp3-bar-title">{productHeadingText()}</span>
                    <span className={`pp3-bar-picks${open.length > 0 && redesign.tried ? ' missed' : ''}`}>{note}</span>
                </p>
                <p className={`pp3-bar-ship pp3-ship-${ship}${open.length > 0 ? ' has-questions' : ''}`}>
                    <TruckIcon size={16} />
                    {shipShort(ship)}
                </p>
                {open.length > 0 && <p className={`pp3-bar-questions${redesign.tried ? ' missed' : ''}`}>{questionsLeft(open.length)}</p>}
                {s.ui.show_pricing && (
                    <p
                        className="pp3-total"
                        aria-busy={s.ui.pricing_loading}
                    >
                        <span>Your total</span>
                        <strong>{price.current}</strong>
                    </p>
                )}
                {s.ui.show_atc && (
                    <div className="pp3-atc">
                        <ATCButton
                            additionalClass="pp3-atc-btn pp3-bar-atc"
                            beforeAdd={beforeRedesignAddToCart}
                        />
                    </div>
                )}
                {s.ui.show_atc && s.ps.atc_error && s.ps.atc_error !== '' && (
                    <p
                        className="pp3-bar-error"
                        role="status"
                    >
                        {s.ps.atc_error}
                    </p>
                )}
            </div>
        </div>
    );
});

export default PinnedBar;
